/**
 * Demo gate utilities.
 *
 * The gate ONLY activates on the production casey-tools.com and
 * caseylegaltools.com hosts (apex + www).
 * Subdomains (journals.casey-tools.com), Lovable preview URLs, and
 * localhost are all unaffected.
 */

const STORAGE_KEY = "casey:demo-access";
/** Hosts where the gate is enforced. */
const GATED_HOSTS = new Set([
  "casey-tools.com",
  "www.casey-tools.com",
  "caseylegaltools.com",
  "www.caseylegaltools.com",
]);
/** Default cookie/storage TTL when a code has no expires_at: 7 days. */
const DEFAULT_TTL_MS = 7 * 24 * 60 * 60 * 1000;
/**
 * Hidden URL flag the admin can append (?staff=1) to bypass the gate
 * long enough to log in. Persisted via localStorage so the URL only
 * needs to be hit once.
 */
const STAFF_BYPASS_KEY = "casey:staff-bypass";

export const isGatedHost = (): boolean => {
  if (typeof window === "undefined") return false;
  return GATED_HOSTS.has(window.location.hostname.toLowerCase());
};

type StoredGrant = {
  code: string;
  expiresAt: number; // epoch ms
};

export const getStoredGrant = (): StoredGrant | null => {
  if (typeof window === "undefined") return null;
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as StoredGrant;
    if (!parsed?.expiresAt || parsed.expiresAt <= Date.now()) {
      window.localStorage.removeItem(STORAGE_KEY);
      return null;
    }
    return parsed;
  } catch {
    return null;
  }
};

export const setStoredGrant = (code: string, expiresAt: string | null): void => {
  if (typeof window === "undefined") return;
  const expiry = expiresAt ? new Date(expiresAt).getTime() : Date.now() + DEFAULT_TTL_MS;
  const grant: StoredGrant = { code, expiresAt: expiry };
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(grant));
};

export const clearStoredGrant = (): void => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STORAGE_KEY);
};

/** Has the admin enabled the staff bypass on this device? */
export const hasStaffBypass = (): boolean => {
  if (typeof window === "undefined") return false;
  return window.localStorage.getItem(STAFF_BYPASS_KEY) === "1";
};

export const enableStaffBypass = (): void => {
  if (typeof window === "undefined") return;
  window.localStorage.setItem(STAFF_BYPASS_KEY, "1");
};

export const disableStaffBypass = (): void => {
  if (typeof window === "undefined") return;
  window.localStorage.removeItem(STAFF_BYPASS_KEY);
};

/**
 * Pick up ?staff=1 from the URL the first time it's used and remember it.
 * Strips the query param from the URL so it doesn't get shared.
 */
export const consumeStaffBypassFromUrl = (): void => {
  if (typeof window === "undefined") return;
  const url = new URL(window.location.href);
  if (url.searchParams.get("staff") === "1") {
    enableStaffBypass();
    url.searchParams.delete("staff");
    window.history.replaceState({}, "", url.toString());
  }
};

/**
 * True when the visitor is in browse-only demo mode (valid code but no
 * authenticated session). Used to hide CTAs and disable mutations.
 */
export const isDemoVisitorMode = (isAuthed: boolean): boolean => {
  if (!isGatedHost()) return false;
  if (isAuthed) return false;
  return !!getStoredGrant();
};
